import { __awaiter, __extends, __generator } from "tslib";
import { FailedReason, invalidDomainErrorMessage, invalidUrlErrorMessage } from './constants';
import { AppInitState, } from './hosted-web-content-sdk.interface';
import { LogLevel } from './interfaces';
import { MessageProcessor } from './MessageHandling/message-processor';
import { formatUrlWithFullContext, getInvalidDomains, getResourceForApp, getValidDomainsForApp, isValidResourceForPrefetch, isValidUrlForApp, removeDefaultPort, transformContext, } from './utils';
import { getCurrentFeatureFlagsState } from './utils/featureFlags';
import { getPageDisplayNameFromAppDefinition, getStateToSave } from './utils/helpers';
import { WebContentSdkService } from './web-content-sdk';
var HostedWebContentSdkService = /** @class */ (function (_super) {
    __extends(HostedWebContentSdkService, _super);
    function HostedWebContentSdkService(
    // TODO: Resolve inability to access global window through IWindowProvider https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/746287
    hostWindow, communicationBridge, context, hubServices, onAppInitStateChanged, hostVersionsInfo) {
        var _this = _super.call(this, hostWindow, context, hubServices.authenticationService, hubServices.logger, hubServices.telemetryService, onAppInitStateChanged) || this;
        _this.hostWindow = hostWindow;
        _this.communicationBridge = communicationBridge;
        _this.context = context;
        _this.hubServices = hubServices;
        _this.onAppInitStateChanged = onAppInitStateChanged;
        _this.hostVersionsInfo = hostVersionsInfo;
        _this.isMetaOSMounted = false;
        _this.isCachedLaunch = false;
        _this.getAppDefinition = function () { return _this.appDefinition; };
        /**
         * gets the origin of the hosted app from bridge
         */
        _this.getOrigin = function () { return _this.communicationBridge.getOrigin(); };
        _this.getDisplayName = function () {
            var _a, _b;
            return ((_a = _this.context.page.config) === null || _a === void 0 ? void 0 : _a.instanceId)
                ? (_b = _this.context.page.config) === null || _b === void 0 ? void 0 : _b.displayName
                : _this.appDefinition && getPageDisplayNameFromAppDefinition(_this.appDefinition, _this.context.page.id);
        };
        /**
         * Request authentication token(s) during loading
         *
         * @description
         * prefetchAuthToken does not return an auth token. It only calls an authentication service acquireToken method.
         * We are expecting a Hubs authentication service to handle the caching of tokens.
         */
        _this.prefetchAuthToken = function (appContext, appDefinition) { return __awaiter(_this, void 0, void 0, function () {
            var scenario, resourceForApp, ex_1, errorMessage, errorMessage;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        scenario = (_a = this.telemetryService) === null || _a === void 0 ? void 0 : _a.createScenario('preFetchAuthToken', appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.id);
                        if (!this.hubServices.authenticationService) return [3 /*break*/, 6];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 4, , 5]);
                        resourceForApp = getResourceForApp(appDefinition, appContext);
                        if (!(resourceForApp && isValidResourceForPrefetch(resourceForApp))) return [3 /*break*/, 3];
                        // Hub's authentication service handles token fetch and caching
                        return [4 /*yield*/, this.hubServices.authenticationService.acquireToken(resourceForApp, undefined, true)];
                    case 2:
                        // Hub's authentication service handles token fetch and caching
                        _b.sent();
                        scenario === null || scenario === void 0 ? void 0 : scenario.stop();
                        return [2 /*return*/, true];
                    case 3: return [3 /*break*/, 5];
                    case 4:
                        ex_1 = _b.sent();
                        errorMessage = 'Unable to prefetch token via authentication service: ' + ex_1;
                        this.getLogger().log(LogLevel.Error, 'prefetchAuthToken', errorMessage, ex_1);
                        scenario === null || scenario === void 0 ? void 0 : scenario.fail(errorMessage);
                        return [3 /*break*/, 5];
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        errorMessage = 'Cancelled since authenticationService is not available';
                        this.getLogger().log(LogLevel.Error, 'prefetchAuthToken', errorMessage);
                        scenario === null || scenario === void 0 ? void 0 : scenario.fail(errorMessage);
                        _b.label = 7;
                    case 7: return [2 /*return*/, false];
                }
            });
        }); };
        _this.urlToLoad = function (formattedUrl) { return formattedUrl; };
        _this.setAppInitInfo = function (appInitState) {
            _this.checkIfCacheableAndInvoke(function (cacheablePageInfo) {
                var _a;
                (_a = _this.hubServices.cacheManagementService) === null || _a === void 0 ? void 0 : _a.setAppState(cacheablePageInfo, appInitState);
            });
            // cannot use super in arrow functions
            if (_this.onAppInitStateChanged) {
                _this.onAppInitStateChanged(appInitState);
            }
        };
        _this.loadCacheableRegistrationState = function () {
            if (!_this.hubServices.cacheManagementService) {
                return;
            }
            var cacheablePageInfo = _this.getCacheablePageInfo();
            return (cacheablePageInfo && _this.hubServices.cacheManagementService.loadBootstrapRegistrationState(cacheablePageInfo));
        };
        _this.saveCacheableRegistrationState = function (registrationType, settingTypes) {
            _this.checkIfCacheableAndInvoke(function (cacheablePageInfo) {
                var _a;
                (_a = _this.hubServices.cacheManagementService) === null || _a === void 0 ? void 0 : _a.saveBootstrapRegistrationState(cacheablePageInfo, getStateToSave(registrationType, settingTypes));
            });
        };
        _this.checkIfCachedAndRequestAllowed = function (message) {
            if (!_this.hubServices.cacheManagementService || !_this.hubServices.cacheManagementService.isCachedAndAllowRequest) {
                return true;
            }
            var cacheablePageInfo = _this.getCacheablePageInfo();
            if (!cacheablePageInfo) {
                return true;
            }
            return _this.hubServices.cacheManagementService.isCachedAndAllowRequest(cacheablePageInfo, message);
        };
        _this.unloadCachedApp = function (cacheablePageInfo) {
            // TODO: unregister event handlers and dispose all services to minimize memory impact when cached
            // https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/2022311
            var cacheManagementService = _this.hubServices.cacheManagementService;
            // after readyToUnload or Timeout, CacheManagementService will remove App if the App cannot be cached
            // or does not support Load/beforeUnload
            var shouldTriggerUnload = cacheManagementService === null || cacheManagementService === void 0 ? void 0 : cacheManagementService.shouldTriggerUnload(cacheablePageInfo);
            if (shouldTriggerUnload) {
                cacheManagementService === null || cacheManagementService === void 0 ? void 0 : cacheManagementService.setAppState(cacheablePageInfo, { state: AppInitState.Unloading });
                _this.sendRequestMessage('beforeUnload');
            }
            else {
                cacheManagementService === null || cacheManagementService === void 0 ? void 0 : cacheManagementService.setAppState(cacheablePageInfo, { state: AppInitState.Unloaded });
            }
        };
        /**
         * Clean up communication bridge with App SDK
         */
        _this.disposeBridge = function () {
            _this.communicationBridge.dispose();
        };
        /**
         * Wrapper that will invoke passed function only if conditions to support Caching are met
         * @param fnToInvoke function to ivoke if cacheManagmentService and appDefinition are valid
         * @param altFn function to ivoke if cacheManagmentService or appDefinition is not valid
         */
        _this.checkIfCacheableAndInvoke = function (fnToInvoke, altFn) {
            if (_this.hubServices.cacheManagementService) {
                var cacheablePageInfo = _this.getCacheablePageInfo();
                cacheablePageInfo ? fnToInvoke(cacheablePageInfo) : altFn === null || altFn === void 0 ? void 0 : altFn();
            }
            else {
                altFn === null || altFn === void 0 ? void 0 : altFn();
            }
        };
        _this.getCacheablePageInfo = function () {
            var _a;
            return _this.appDefinition
                ? {
                    appId: _this.appDefinition.id,
                    entityId: _this.context.page.id,
                    displayName: _this.getDisplayName(),
                    instanceId: (_a = _this.context.page.config) === null || _a === void 0 ? void 0 : _a.instanceId,
                    contentUrl: _this.getOrigin(),
                }
                : undefined;
        };
        _this.isMetaOSMounted = true;
        /* init auth context before loading any app url in iframe :
           This ensures auth Service to be linked in for user details before use Context for placeholding
        */
        _this.initializeAuthenticationContext(context);
        communicationBridge.onSdkInitialized && communicationBridge.onSdkInitialized(hostWindow);
        return _this;
    }
    /**
     * initialize frame and load target origin to frame
     * @param frame target frame
     * @param origin target origin
     * @param appDefinition Hosted web content app definition
     */
    HostedWebContentSdkService.prototype.setFrameAndLoadOrigin = function (frame, origin, appDefinition, userClickTime, usingCachedApp) {
        return __awaiter(this, void 0, void 0, function () {
            var bootstrapRegistrationState, resolvedAppContext, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        this.isCachedLaunch = usingCachedApp || false;
                        this.appDefinition = appDefinition;
                        this.setAppInitInfo({ state: AppInitState.Loading });
                        // When reactivating a Cached App, set Initialized to true as App wont be invoking Initialize again
                        this.setInitialized(!!usingCachedApp);
                        this.telemetryService.setAppDefinition(appDefinition);
                        this.frame = frame;
                        this.setUserClickTime(userClickTime);
                        if (this.messageProcessor) {
                            this.messageProcessor.cleanup();
                        }
                        bootstrapRegistrationState = void 0;
                        if (usingCachedApp) {
                            bootstrapRegistrationState = this.loadCacheableRegistrationState();
                        }
                        return [4 /*yield*/, this.getAppContext()];
                    case 1:
                        resolvedAppContext = _a.sent();
                        // prefetch token asynchronously
                        this.prefetchAuthToken(resolvedAppContext, appDefinition);
                        this.messageProcessor = new MessageProcessor(this, this.hubServices, this.telemetryService, bootstrapRegistrationState, this.hostVersionsInfo);
                        // try loading App
                        this.load(origin, resolvedAppContext, usingCachedApp);
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _a.sent();
                        this.getLogger().log(LogLevel.Error, 'setFrameAndLoadOrigin', 'An error occurred while loading URL', e_1);
                        this.handleAppLoadFailure(e_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Handles the lifecycle and maintenance incase of app load failure
     */
    HostedWebContentSdkService.prototype.handleAppLoadFailure = function (error) {
        var _a, _b;
        var failedReason;
        if (error === invalidDomainErrorMessage) {
            failedReason = FailedReason.Validation_InvalidDomains;
        }
        else {
            failedReason = FailedReason.Validation;
        }
        this.setAppInitInfo({ state: AppInitState.Failed, reason: failedReason });
        // fail telemetry
        (_a = this.getAppInitializationScenario()) === null || _a === void 0 ? void 0 : _a.fail(failedReason);
        // clean up
        (_b = this.messageProcessor) === null || _b === void 0 ? void 0 : _b.cleanup();
    };
    /**
     * to send request message to frame
     * @param functionName function name
     * @param funArguments function arguments
     */
    HostedWebContentSdkService.prototype.sendRequestMessage = function (functionName, funArguments) {
        if (this.getOrigin() && this.frame) {
            var request = {
                func: functionName,
                args: funArguments,
            };
            this.communicationBridge.postMessage(this.frame, request);
        }
    };
    /**
     * to send response message to frame
     * @param target target
     * @param id id used for callback function
     * @param args function arguments
     */
    HostedWebContentSdkService.prototype.sendResponseMessage = function (target, id, args, isPartialResponse) {
        if (this.getOrigin()) {
            var response = {
                id: id,
                args: args,
                isPartialResponse: isPartialResponse,
                origin: this.getOrigin(),
            };
            this.communicationBridge.postMessage(target, response);
        }
    };
    /**
     * sets the origin of the hosted app in the bridge
     */
    HostedWebContentSdkService.prototype.setOrigin = function (origin) {
        this.communicationBridge.setOrigin(origin);
    };
    /**
     * to send response message to app frame
     * @param id id used for callback function
     * @param args function arguments
     * @param isPartialResponse is response partial
     */
    HostedWebContentSdkService.prototype.postResponseToApp = function (id, args, isPartialResponse) {
        if (this.frame) {
            this.sendResponseMessage(this.frame, id, args, isPartialResponse);
        }
    };
    /**
     * handles the given message event using one of the built in SDK message
     * handlers, or one previously provided via `addMessageHandler`
     *
     * @protected
     * @memberof HostedWebContentSdkService
     */
    HostedWebContentSdkService.prototype.handleMessage = function (event) {
        if (!this.messageProcessor) {
            throw new Error('Unable to handle message. Undefined message processor');
        }
        this.messageProcessor.process(event);
    };
    /**
     * load origin to frame
     * @param url target url
     */
    HostedWebContentSdkService.prototype.load = function (url, appContext, isCachedApp) {
        if (isCachedApp === void 0) { isCachedApp = false; }
        if (!this.isMetaOSMounted) {
            return;
        }
        // Establish communication before loading url
        if (this.frame && this.appDefinition) {
            this.communicationBridge.onSetFrameAndLoadOrigin(this.frame, url, this.appDefinition, appContext, this.handleMessage.bind(this));
        }
        // validate and load URL
        if (isValidUrlForApp(url, this.getLogger(), this.getAppDefinition(), appContext) && this.frame) {
            var transformedContext = transformContext(appContext);
            var formattedUrl = formatUrlWithFullContext(url, appContext, transformedContext);
            this.getLogger().log(LogLevel.Debug, 'load', "origin: ".concat(formattedUrl));
            this.setOrigin(formattedUrl);
            // set to waiting
            this.setAppInitInfo({ state: AppInitState.WaitingForAppLoad });
            // Dont set the src when loading Cached App as that will cause the webview to be reloaded
            if (!isCachedApp) {
                var parsedUrl = new URL(formattedUrl);
                this.frame.src = removeDefaultPort(this.urlToLoad(parsedUrl));
            }
            else {
                this.sendRequestMessage('load', [
                    {
                        entityId: appContext.page.id,
                        contentUrl: this.getOrigin(),
                    },
                ]);
            }
        }
        else {
            if (getCurrentFeatureFlagsState().wildcardValidDomainRuleDetection &&
                this.getAppDefinition() &&
                getInvalidDomains(getValidDomainsForApp(this.getAppDefinition(), appContext)).length > 0) {
                throw invalidDomainErrorMessage;
            }
            else {
                throw invalidUrlErrorMessage;
            }
        }
    };
    HostedWebContentSdkService.prototype.supportsLoad = function () {
        var _this = this;
        this.checkIfCacheableAndInvoke(function (cacheablePageInfo) { var _a; return (_a = _this.hubServices.cacheManagementService) === null || _a === void 0 ? void 0 : _a.supportsLoad(cacheablePageInfo); });
    };
    HostedWebContentSdkService.prototype.supportsBeforeUnload = function () {
        var _this = this;
        this.checkIfCacheableAndInvoke(function (cacheablePageInfo) { var _a; return (_a = _this.hubServices.cacheManagementService) === null || _a === void 0 ? void 0 : _a.supportsBeforeUnload(cacheablePageInfo); });
    };
    /**
     * function to execute when app is unloaded or sdk is unloaded
     */
    HostedWebContentSdkService.prototype.unloadApp = function (appDefinition) {
        var _a;
        _super.prototype.unloadApp.call(this, appDefinition);
        (_a = this.messageProcessor) === null || _a === void 0 ? void 0 : _a.cleanup();
    };
    /**
     * function to execute when app container is unmounted
     * Clean up message processing pipeline
     * If App Caching is not supported, dispose communication bridge right away
     * If App Caching is supported, communication bridge can only be disposed when App is removed from the Cache
     */
    HostedWebContentSdkService.prototype.unloadSDK = function () {
        var _a;
        (_a = this.messageProcessor) === null || _a === void 0 ? void 0 : _a.cleanup();
        this.isMetaOSMounted = false;
        this.onAppInitStateChanged = undefined; // release reference to setter function
        if (this.hubServices.cacheManagementService) {
            // When switching between instances of an App, if the component is unmounted before the call to setFrameAndLoadOrigin
            // cacheablePageInfo will be undefined as appDefinition is not set. This can only happen if we are in the
            // "unloading" state waiting for App to send "readyToUnload"
            // In this case, don't dispose the bridge during unmount (the timeout logic will evict the App from the cache if needed)
            var cacheablePageInfo = this.getCacheablePageInfo();
            cacheablePageInfo && this.unloadCachedApp(cacheablePageInfo);
        }
        else {
            this.disposeBridge();
        }
    };
    HostedWebContentSdkService.prototype.getFrame = function () {
        return this.frame;
    };
    return HostedWebContentSdkService;
}(WebContentSdkService));
export { HostedWebContentSdkService };
//# sourceMappingURL=hosted-web-content-sdk.js.map